import React, { createContext, useContext, useState, ReactNode } from 'react'
import ToastSnackbar from '../Components/Common/ToastSnackbar'

// Define the toast type
type ToastType = {
  id: number
  severity: 'success' | 'error' | 'warning' | 'info'
  message: string
}

// Define the context type
type ToastContextType = (type: 'success' | 'error' | 'warning' | 'info', msg: string) => void

// Create the Toast context
const ToastContext = createContext<ToastContextType | undefined>(undefined)

// Custom hook to use the Toast context
export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

// Define the props for the ToastProvider
interface ToastProviderProps {
  children: ReactNode
}

// ToastProvider component
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastType[]>([])

  const showToast: ToastContextType = (type, msg) => {
    const id = new Date().getTime() // Create a unique ID for each toast
    setToasts((prev) => [...prev, { id, severity: type, message: msg }])
  }

  const handleClose = (id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      {toasts.map((toast) => (
        <ToastSnackbar
          key={toast.id}
          open={true}
          severity={toast.severity}
          message={toast.message}
          onClose={() => handleClose(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  )
}
